"use client";

import * as React from "react";
import { Button } from "./button";
import { Icon } from "./icon";
import type { InputProps } from "./input";
import { InputGroup, InputGroupAddon, InputGroupInput } from "./input-group";

/** `bare` has no meaning here: it strips the shell, and the shell is what
 *  holds the visibility toggle. The chrome props stay, and they dress the
 *  group. */
export type PasswordInputProps = Omit<
  InputProps,
  "type" | "unstyled" | "nativeInput" | "bare"
>;

/**
 * A password field with a visibility toggle: an InputGroup whose trailing
 * ghost button flips the control between password and text. The flip is
 * presentation only — the value and caret stay where they are.
 */
export function PasswordInput({
  size,
  variant,
  backgroundColor,
  borderColor,
  ...props
}: PasswordInputProps): React.ReactElement {
  const [visible, setVisible] = React.useState(false);
  // The caret to put back after the flip: the browser resets the selection
  // when an input's `type` changes, a frame after the commit.
  const pendingSelection = React.useRef<{
    input: HTMLInputElement;
    start: number | null;
    end: number | null;
  } | null>(null);
  React.useLayoutEffect(() => {
    const pending = pendingSelection.current;
    if (!pending) return;
    pendingSelection.current = null;
    const restore = () => pending.input.setSelectionRange(pending.start, pending.end);
    restore();
    const frame = requestAnimationFrame(restore);
    return () => cancelAnimationFrame(frame);
  }, [visible]);

  return (
    // The chrome rides the group: the inner control is unstyled, so a
    // treatment left on it would paint nothing.
    <InputGroup
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      variant={variant}
    >
      <InputGroupInput type={visible ? "text" : "password"} size={size} {...props} />
      <InputGroupAddon align="inline-end">
        <Button
          type="button"
          variant="ghost"
          // The toggle tracks the control height: the 44px/40px xl field
          // takes the 32px/28px button so both insets read ~6px.
          size={size === "xl" ? "icon-sm" : "icon-xs"}
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          // A press must not take focus from the field: on a phone that
          // would drop the keyboard and bring it straight back.
          onPointerDown={(event) => event.preventDefault()}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            const input = event.currentTarget
              .closest('[data-slot="input-group"]')
              ?.querySelector<HTMLInputElement>('input[data-slot="input"]');
            if (input && document.activeElement === input) {
              pendingSelection.current = {
                input,
                start: input.selectionStart,
                end: input.selectionEnd,
              };
            }
            setVisible((v) => !v);
          }}
        >
          <Icon name={visible ? "eye-closed" : "eye"} />
        </Button>
      </InputGroupAddon>
    </InputGroup>
  );
}
